import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, fmt } from "@/lib/api";

const STATUSES = ["awaiting_payment", "pending", "paid", "shipped", "cancelled"];

const COLOR = {
  awaiting_payment: "border-amber-400/40 text-amber-400",
  pending: "border-amber-400/30 text-amber-400",
  paid: "border-cyan-400/30 text-cyan-400",
  shipped: "border-green-400/30 text-green-400",
  cancelled: "border-red-400/30 text-red-400",
};

const LABEL = {
  awaiting_payment: "awaiting payment",
  pending: "pending",
  paid: "paid",
  shipped: "shipped",
  cancelled: "cancelled",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);

  const update = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}`, { status });
      toast.success(status === "paid" ? "Marked as paid · emails sent" : "Order updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-orders">
      <h2 className="text-2xl font-medium mb-6">Orders</h2>
      {orders.length === 0 && <p className="text-slate-500">No orders yet.</p>}
      <div className="space-y-4">
        {orders.map((o) => (
          <div key={o.id} className="border border-white/5 p-5" data-testid={`admin-order-${o.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-mono text-xs text-slate-500">#{o.id.slice(0, 8).toUpperCase()}</div>
                <div className="text-sm">{o.email}</div>
                <div className="text-xs text-slate-500 mt-1">{new Date(o.created_at).toLocaleString()}</div>
              </div>
              <div className={`label-mono px-3 py-1 border ${COLOR[o.status]}`}>{LABEL[o.status] || o.status}</div>
              <div className="font-mono text-lg text-cyan-400">{fmt(o.total)}</div>
              {o.status === "awaiting_payment" && (
                <button
                  onClick={() => update(o.id, "paid")}
                  data-testid={`mark-paid-${o.id}`}
                  className="inline-flex items-center gap-1.5 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm px-3 py-2"
                >
                  <CheckCircle2 size={14} /> Mark paid
                </button>
              )}
              <select
                data-testid={`status-select-${o.id}`}
                value={o.status}
                onChange={(e) => update(o.id, e.target.value)}
                className="bg-slate-950 border border-white/10 text-sm px-3 py-2 focus:border-cyan-500 outline-none"
              >
                {STATUSES.map((s) => <option key={s} value={s}>{LABEL[s] || s}</option>)}
              </select>
            </div>
            {o.payment_reference && (
              <div className="mt-2 font-mono text-xs text-cyan-300/70">REF: {o.payment_reference} · via {o.payment_method || "—"}</div>
            )}
            <div className="mt-4 text-xs text-slate-400 space-y-1">
              {o.items.map((i, idx) => (
                <div key={idx} className="flex justify-between">
                  <span>{i.name} ×{i.quantity}</span>
                  <span className="font-mono">{fmt(i.line_total)}</span>
                </div>
              ))}
            </div>
            {o.shipping_address?.full_name && (
              <div className="mt-4 text-xs text-slate-500 border-t border-white/5 pt-3">
                Ship to: {o.shipping_address.full_name}, {o.shipping_address.address1}, {o.shipping_address.city}, {o.shipping_address.country}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
